import type { ReactElement } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../../../../shared/components/ui/select";

interface RatingSelectProps {
  readonly value: string | null;
  readonly onChange: (rating: string | null) => void;
  readonly disabled?: boolean;
  readonly triggerClassName?: string;
  readonly textClassName?: string;
  readonly placeholder?: string;
  readonly contentClassName?: string;
}

/**
 * Common content ratings
 * MPA (movies) and TV ratings combined
 */
const RATINGS = [
  { value: "G", label: "G - General Audiences", type: "movie" },
  { value: "PG", label: "PG - Parental Guidance", type: "movie" },
  { value: "PG-13", label: "PG-13 - Parents Strongly Cautioned", type: "movie" },
  { value: "R", label: "R - Restricted", type: "movie" },
  { value: "NC-17", label: "NC-17 - Adults Only", type: "movie" },
  { value: "TV-Y", label: "TV-Y - All Children", type: "tv" },
  { value: "TV-Y7", label: "TV-Y7 - Older Children", type: "tv" },
  { value: "TV-G", label: "TV-G - General Audience", type: "tv" },
  { value: "TV-PG", label: "TV-PG - Parental Guidance", type: "tv" },
  { value: "TV-14", label: "TV-14 - Parents Strongly Cautioned", type: "tv" },
  { value: "TV-MA", label: "TV-MA - Mature Audience", type: "tv" },
  { value: "NR", label: "NR - Not Rated", type: "general" },
];

/**
 * Select component for content rating
 * Supports both MPA (movie) and TV ratings
 */
export function RatingSelect({
  value,
  onChange,
  disabled,
  triggerClassName = "w-[80px] h-8 border-none",
  textClassName = "text-xs",
  placeholder = "Rating",
  contentClassName,
}: Readonly<RatingSelectProps>): ReactElement {
  const handleValueChange = (newValue: string) => {
    onChange(newValue === "none" ? null : newValue);
  };

  const displayValue = value ?? placeholder;

  return (
    <Select value={value ?? "none"} onValueChange={handleValueChange} disabled={disabled}>
      <SelectTrigger className={triggerClassName}>
        <div className="flex items-center gap-2">
          <span className={`${textClassName} font-medium`}>{displayValue}</span>
        </div>
      </SelectTrigger>
      <SelectContent className={contentClassName}>
        <SelectItem value="none">
          <span className="px-2 py-1 text-xs font-semibold text-neutral-400 italic">None</span>
        </SelectItem>
        <div className="px-2 py-1 text-xs font-semibold text-neutral-400">Movie Ratings</div>
        {RATINGS.filter((r) => r.type === "movie").map(({ value: ratingValue, label }) => (
          <SelectItem key={ratingValue} value={ratingValue} className="px-4 py-1 text-xs font-medium text-neutral-100">
            {label}
          </SelectItem>
        ))}
        <div className="px-2 py-1 text-xs font-semibold text-neutral-400 mt-2">TV Ratings</div>
        {RATINGS.filter((r) => r.type === "tv").map(({ value: ratingValue, label }) => (
          <SelectItem key={ratingValue} value={ratingValue} className="px-4 py-1 text-xs font-medium text-neutral-100">
            {label}
          </SelectItem>
        ))}
        <div className="px-2 py-1 text-xs font-semibold text-neutral-400 mt-2">Other</div>
        {RATINGS.filter((r) => r.type === "general").map(({ value: ratingValue, label }) => (
          <SelectItem key={ratingValue} value={ratingValue} className="px-4 py-1 text-xs font-medium text-neutral-100">
            {label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
